import React, { useState } from "react";
import { useLocation } from "react-router-dom";
import { Star, X } from "lucide-react";
import api from "@/lib/api";

function alreadyRated(deliverable, entityKey) {
  try {
    return !!localStorage.getItem(`or_dfb_done:${deliverable}:${entityKey}`);
  } catch {
    return false;
  }
}

function markRated(deliverable, entityKey) {
  try {
    localStorage.setItem(`or_dfb_done:${deliverable}:${entityKey}`, "1");
  } catch {
    /* storage unavailable - fine */
  }
}

/**
 * Micro-feedback card shown once after a completed deliverable.
 * One-tap star rating (submitted immediately), optional comment, and for
 * 4-5 star ratings an opt-in to use the comment as a testimonial.
 * Compact, dismissible, never blocks the user.
 */
export const DeliverableFeedback = ({ deliverable, label, entityKey, context }) => {
  const location = useLocation();
  const [hidden, setHidden] = useState(() => !entityKey || alreadyRated(deliverable, entityKey));
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [feedbackId, setFeedbackId] = useState(null);
  const [comment, setComment] = useState("");
  const [testimonialOk, setTestimonialOk] = useState(false);
  const [done, setDone] = useState(false);

  if (hidden) return null;

  const rate = async (value) => {
    setRating(value);
    markRated(deliverable, entityKey);
    try {
      const { data } = await api.post("/feedback", {
        category: "deliverable_rating",
        rating: value,
        page: location.pathname,
        context: { ...(context || {}), deliverable, entity: entityKey },
      });
      setFeedbackId(data.feedback_id);
    } catch {
      /* best-effort - never block */
    }
  };

  const sendComment = async () => {
    const text = comment.trim();
    if (feedbackId && text) {
      try {
        await api.post(`/feedback/${feedbackId}/append`, {
          text,
          testimonial_ok: rating >= 4 ? testimonialOk : false,
        });
      } catch {
        /* best-effort */
      }
    }
    setDone(true);
    setTimeout(() => setHidden(true), 1600);
  };

  const dismiss = () => {
    if (entityKey) markRated(deliverable, entityKey);
    setHidden(true);
  };

  return (
    <div
      data-testid={`deliverable-feedback-${deliverable}`}
      className="rounded-xl border border-zinc-800 bg-zinc-900/40 px-4 py-3"
    >
      {done ? (
        <div className="text-xs text-emerald-300" data-testid="deliverable-feedback-thanks">
          Thanks, your feedback helps us improve Intuiterview.
        </div>
      ) : rating === 0 ? (
        <div className="flex items-center gap-3 flex-wrap">
          <div className="text-xs text-zinc-300 flex-1 min-w-[180px]">
            How useful was your {label}?
          </div>
          <div className="flex items-center gap-1">
            {[1, 2, 3, 4, 5].map((v) => (
              <button
                key={v}
                type="button"
                data-testid={`deliverable-feedback-star-${v}`}
                onClick={() => rate(v)}
                onMouseEnter={() => setHover(v)}
                onMouseLeave={() => setHover(0)}
                aria-label={`${v} star${v > 1 ? "s" : ""}`}
                className="p-0.5"
              >
                <Star
                  className={`h-4.5 w-4.5 h-[18px] w-[18px] transition-colors ${
                    v <= hover ? "text-amber-300 fill-amber-300/60" : "text-zinc-600"
                  }`}
                />
              </button>
            ))}
            <button
              type="button"
              data-testid="deliverable-feedback-dismiss"
              onClick={dismiss}
              aria-label="Dismiss"
              className="p-1 ml-1 rounded-md text-zinc-600 hover:text-zinc-300"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
      ) : (
        <div className="space-y-2.5">
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-0.5">
              {[1, 2, 3, 4, 5].map((v) => (
                <Star
                  key={v}
                  className={`h-[14px] w-[14px] ${v <= rating ? "text-amber-300 fill-amber-300/60" : "text-zinc-700"}`}
                />
              ))}
            </div>
            <span className="text-[11px] text-zinc-500">Thanks! Anything you'd like us to improve? (optional)</span>
          </div>
          <textarea
            data-testid="deliverable-feedback-comment"
            rows={2}
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="Your comment…"
            className="w-full bg-zinc-950 border border-zinc-800 rounded-lg px-3 py-2 text-xs text-zinc-100 placeholder:text-zinc-600 focus:border-zinc-700 focus:outline-none"
          />
          <div className="flex items-center justify-between gap-3 flex-wrap">
            {rating >= 4 && comment.trim() ? (
              <label className="flex items-center gap-1.5 text-[11px] text-zinc-500 cursor-pointer">
                <input
                  type="checkbox"
                  data-testid="deliverable-feedback-testimonial-ok"
                  checked={testimonialOk}
                  onChange={(e) => setTestimonialOk(e.target.checked)}
                  className="accent-amber-500"
                />
                You may use my comment as a testimonial
              </label>
            ) : (
              <span />
            )}
            <div className="flex items-center gap-2">
              <button
                type="button"
                data-testid="deliverable-feedback-skip"
                onClick={() => { setDone(true); setTimeout(() => setHidden(true), 1200); }}
                className="text-[11px] text-zinc-500 hover:text-zinc-300"
              >
                Skip
              </button>
              <button
                type="button"
                data-testid="deliverable-feedback-send"
                onClick={sendComment}
                className="text-xs bg-amber-500 hover:bg-amber-400 text-amber-950 font-semibold rounded-md px-3 py-1"
              >
                Send
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DeliverableFeedback;
